import React, { createContext, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { showError } from 'src/components/alertNotification';
import { CLIENT_LOGIN, CLIENT_PORTAL_LIST } from 'src/constants';
import {
  IClientDetailDto,
  IOtpLoginMutation,
  useOtpLoginMutation,
  useVerifyOtpMutation,
} from 'src/graphql/auto-generated/graphql';
import { IEndPoint } from 'src/config/apolloClient';
import TokenService from 'src/services/token-storage';

export interface ILogin {
  email: string;
  fingerprint: string;
  fromLink?: string;
  clientId?: string;
}

interface IVerifyOtp {
  email: string;
  sessionToken: string;
  otp: string;
  fingerprint: string;
  clientId?: string;
  fromLink?: string;
}
interface ClientAuthContextType {
  login: (value: ILogin) => Promise<IOtpLoginMutation | null | undefined | null>;
  logout: (linkPath?: string) => void;
  verifyOtp: (value: IVerifyOtp) => void;
  loginLoading: boolean;
  verifyOtpLoading: boolean;
  isAuthenticated: boolean;
  otpLoginData: IOtpLoginMutation | null | undefined;
}

export const ClientAuthContext = createContext<ClientAuthContextType>({
  login: async () => null,
  verifyOtp: async () => {},
  logout: async () => {},
  loginLoading: false,
  verifyOtpLoading: false,
  isAuthenticated: false,
  otpLoginData: null,
});

export const ClientAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!TokenService.getAccessToken());

  const { push, replace } = useHistory();

  const [otpLogin, { loading: loginLoading, data: otpLoginData }] = useOtpLoginMutation({
    context: {
      endpoint: IEndPoint.MANAGE,
      skipAuth: true,
    },
  });

  const [otpVerify, { loading: verifyOtpLoading }] = useVerifyOtpMutation({
    context: {
      endpoint: IEndPoint.MANAGE,
      skipAuth: true,
    },
    onError: error => {
      showError(error.message);
    },
    onCompleted: async data => {
      await TokenService.setAccessToken(data?.verifyOtp?.accessToken ?? '');
      TokenService.setRefreshToken(data?.verifyOtp?.refreshToken ?? '');
      setIsAuthenticated(true);
      TokenService.setUserDetails(data?.verifyOtp?.userInfo ?? '');
      if (data?.verifyOtp?.clientIdsEncoded?.trim() === '') {
        TokenService?.setClientDetails({ clientInfo: data?.verifyOtp?.clientInfo as IClientDetailDto });
      } else {
        TokenService?.setClientDetails({ clientIdsEncoded: data?.verifyOtp?.clientIdsEncoded as string });
      }
    },
  });

  const login = async (value: ILogin): Promise<any | null> => {
    try {
      const response = await otpLogin({ variables: { otpLoginArgs: { ...value } } });
      setIsAuthenticated(true);
      return response?.data ?? null;
    } catch (e) {
      if (e instanceof ApolloError) {
        const { message } = e;
        if (message) showError(message);
      }
      return null;
    }
  };

  const verifyOtp = async (value: IVerifyOtp) => {
    try {
      const requiredValue = { ...value };
      delete requiredValue.fromLink;
      await otpVerify({
        variables: { verfiyOtpArgs: { ...requiredValue } },
      });

      if (value.fromLink) {
        replace(value.fromLink);
      } else {
        push(CLIENT_PORTAL_LIST);
      }
    } catch (e) {
      if (e instanceof ApolloError) {
        const { message } = e;
        if (message) showError(message);
      }
    }
  };

  const logout = async (linkPath?: string) => {
    await TokenService.clearToken();
    push({
      pathname: CLIENT_LOGIN,
      state: { fromLink: linkPath },
    });
    setIsAuthenticated(false);
  };

  return (
    <ClientAuthContext.Provider
      value={{
        login,
        loginLoading,
        isAuthenticated,
        verifyOtp,
        verifyOtpLoading,
        logout,
        otpLoginData,
      }}
    >
      {children}
    </ClientAuthContext.Provider>
  );
};
